import Helpers from "./helpers";
import Logger from "./logger";
import SelectorFilter from "./selector-filter";
import TrackLists from "./track-lists";
import TrapFocus from "./trap-focus";

declare var Splide: any;
declare var Mustache: any;

export default class AddToBasketOverlay {

	// #region private variables
	private animationTime = 600; // CSS animation time is 500ms, setting this to 600ms allows some breathing room which prevents one animation starting before another has finished (mainly to accomodate firefox)
	private mobileItemWidth = 220; // must match value in CSS
	private state = {
		productId: ''
	};

	private elements = {
		wrapper: null as HTMLElement,
		background: null as HTMLElement,
		main: null as HTMLElement,
		mainInner: null as HTMLElement,
		mainListWrapper: null as HTMLElement,
		mainList: null as NodeList,
		mainItem: null as NodeList,
		mainClose: null as HTMLElement,
		mainBasket: null as HTMLElement,
		skipToBasket: null as HTMLElement,
		mainFooter: null as HTMLElement,
		mainFooterItems: null as HTMLElement,
		mainFooterPrice: null as HTMLElement,
		mainForm: null as NodeList,
		customersAlsoBoughtPlaceholder: null as HTMLElement,
		productQuantity: null as HTMLElement,
		productLink: null as NodeList,
		detail: null as HTMLElement,
		detailFade: null as HTMLElement,
		detailInner: null as HTMLElement,
		detailContainer: null as HTMLElement,
		detailItem: null as HTMLElement,
		detailItemSpecs: null as HTMLElement,
		detailItemSpecsTitle: null as HTMLElement,
		detailItemSpecsContent: null as HTMLElement,
		detailItemView: null as HTMLElement,
		detailClose: null as HTMLElement,
		detailLoader: null as HTMLElement
	}

	private flags = {
		overlayActive: false,
		detailActive: false,
		allowInteraction: true,
		isBasketPage: document.querySelector('.js-new-basket')
	}

	private instances = {
		slideshow: null as any,
		loaderTimeout: 0
	}

	private lockoutTop = 0;

	private mainOverlayTrapFocus: TrapFocus;
	private elementWhichOpenedDetail: HTMLLinkElement;

	private browserInfo = Helpers.getBrowser();
	private lockoutClass = 'html--atb-lockout';

	// #endregion

	constructor() {

		window.addEventListener('orientationchange', () => this.orientationChange);

		document.addEventListener('keyup', (e) => {
			const key = Helpers.getKeypressed(e)
			if (key === 'Escape' || key === 'Esc' || key === 27) {
				this.closeRun(null);
			}
		});

		document.addEventListener('touchmove', (e) => {
			if (this.flags.overlayActive) { e.preventDefault(); }
		});

		document.addEventListener('updateAddedProducts', (e: CustomEvent) => {
			if (this.flags.overlayActive) {
				this.updatePrice();
			}
		});

		if (this.useHeights()) {

			this.lockoutClass = 'html--atb-lockout-fixed-heights';
			const debouncedSetHeights = Helpers.debounce(() => this.setHeights(), 250);
			window.addEventListener('resize', debouncedSetHeights);

		}

		document.addEventListener('closeProductViewer', this.closeDetail.bind(this));

	}

	// Currently only matching devices running Safari 15
	// As well as allowing the settings of heights on <html> and <body> elements, this will also bind and unbind event listeners for "pointermove"
	// Based on a solution found here - https://pqina.nl/blog/how-to-prevent-scrolling-the-page-on-ios-safari/
	useHeights() {
		const userAgent = navigator.userAgent.toLowerCase();
		const isDevice = (userAgent.indexOf('iphone') !== -1) || (userAgent.indexOf('ipad') !== -1);
		return (this.browserInfo.name === 'Safari' && parseInt(this.browserInfo.version) >= 15 && isDevice);
	}

	getTemplate(id: string, data: any) {
		const template = document.getElementById(id).innerHTML;
		return Mustache.render(template, data);
	}

	// #region animations
	allowAnimations() {
		if (this.elements.wrapper) {
			this.elements.wrapper.classList.remove('atb--disallow-animations');
		}
	}

	disallowAnimations() {
		if (this.elements.wrapper) {
			this.elements.wrapper.classList.add('atb--disallow-animations');
		}
	}
	// #endregion

	// #region lockout
	getScrollWidth() {
		let outer = document.createElement("div");
		outer.style.visibility = "hidden";
		outer.style.width = "100px";

		document.body.appendChild(outer);

		let widthNoScroll = outer.offsetWidth;
		outer.style.overflow = "scroll";

		let inner = document.createElement("div");
		inner.style.width = "100%";
		outer.appendChild(inner);

		let widthWithScroll = inner.offsetWidth;
		outer.parentNode.removeChild(outer);

		return widthNoScroll - widthWithScroll;
	}

	preventDefaultEvents(e) {
		e.preventDefault();
	}

	lockoutOn(adjust: boolean) {

		this.lockoutTop = window.scrollY || window.pageYOffset;
		document.documentElement.classList.add(this.lockoutClass);

		if (this.useHeights()) {

			this.elements.wrapper.addEventListener('pointermove', this.preventDefaultEvents);
			this.setHeights();

		} else {

			if (adjust) {
				document.body.style.marginTop = ('-' + this.lockoutTop + 'px');
				document.body.style.marginRight = this.getScrollWidth() + 'px';
			}

		}

	}

	lockoutOff() {

		document.documentElement.classList.remove(this.lockoutClass);

		if (this.useHeights()) {

			this.elements.wrapper.removeEventListener('pointermove', this.preventDefaultEvents);
			this.removeHeights();

		}

		document.body.style.marginTop = '0px';
		document.body.style.marginRight = '0px';
		window.scroll(0, this.lockoutTop);

	}
	// #endregion

	kickScroll(element: HTMLElement) {
		if (!element) {
			return;
		}
		//@ts-ignore
		element.style['-webkit-overflow-scrolling'] = 'hidden';
		element.insertAdjacentHTML('beforeend', '<div data-scroll-kick style="height: 1px"></div>');
		setTimeout(() => {
			//@ts-ignore
			element.style['-webkit-overflow-scrolling'] = 'touch';
			Helpers.removeElement(element.querySelector('[data-scroll-kick]'));
		}, 100);
	}

	// #region close
	closeAnimation() {
		let delay = 0;

		if (this.flags.detailActive) {

			this.elements.detail.classList.remove('c-slidy-outy__detail--active');
			this.elements.main.classList.remove('atb__main--detail-active');
			delay = this.animationTime;

		}

		setTimeout(() => {

			this.flags.detailActive = false;

			this.elements.main.classList.remove('atb__main--active');

			const self = this;

			setTimeout(() => {

				self.elements.wrapper.classList.remove('atb--active');

				setTimeout(() => {
					Helpers.removeElement(self.elements.wrapper);
					self.lockoutOff();
					self.flags.overlayActive = false;
					self.flags.allowInteraction = true;

				}, self.animationTime);

			}, self.animationTime);

		}, delay);
	}

	closeRun(goToUrl: string) {

		if (!this.flags.allowInteraction || !this.flags.overlayActive) return;

		this.flags.allowInteraction = false;
		this.allowAnimations();
		this.mainOverlayTrapFocus.removeListener();

		if ((<any>window).addedProducts && (<any>window).addedProducts.length) {
			const closeATBEvent = Helpers.createCustomEventForDispatch('atbcloseoverlay');
			document.dispatchEvent(closeATBEvent);
		}

		if (goToUrl) {

			window.location.href = goToUrl;

		} else {

			this.closeAnimation();

		}

	}
	// #endregion

	open(data: any) {

		if (!(<any>window).addedProducts) {
			(<any>window).addedProducts = [];
		}
		if (data.AddedProducts && data.AddedProducts.length) {
			for (let i = 0; i < data.AddedProducts.length; i++) {
				const currentProduct = data.AddedProducts[i];
				(<any>window).addedProducts.push({
					id: currentProduct.Id,
					sku: currentProduct.Id,
					quantity: currentProduct.Quantity,
					price: currentProduct.Price.Price
				});
			}
			
		}

		if (!this.flags.allowInteraction) return;

		this.flags.allowInteraction = false;
		this.allowAnimations();

		const addToBasketOverlayHtml = this.getTemplate('addToBasketOverlay', data);
		document.body.insertAdjacentHTML('beforeend', addToBasketOverlayHtml);

		this.setElementsForOverlay();

		if (data.AddedProducts && data.AddedProducts.length && data.AddedProducts[0].Id) {
			this.getCustomersAlsoBought(data.AddedProducts[0].Id);
		}

		setTimeout(() => {

			this.elements.wrapper.classList.add('atb--active');

			setTimeout(() => {

				this.flags.overlayActive = true;
				this.lockoutOn(true);
				this.elements.main.classList.add('atb__main--active');

				setTimeout(() => {

					this.kickScroll(this.elements.mainInner);
					this.kickScroll(this.elements.mainListWrapper);
					this.flags.allowInteraction = true;
					this.disallowAnimations();
					this.focusOnFirstItem();
					this.mainOverlayTrapFocus = new TrapFocus(document.querySelector('.atb__main'), document.querySelector('.atb__main__footer__basket__button'));
					this.updatePrice();
				
					// Tracking
					Helpers.deferTask(() => {
						const listsToTrack = this.elements.main.querySelectorAll('[data-list]');
						for (let i = 0; i < listsToTrack.length; i++) {
							new TrackLists(listsToTrack[i]);
						}
					});
				}, this.animationTime);

			}, this.animationTime);

		}, 1);

		//  events

		this.elements.background.addEventListener('click', () => {
			this.closeRun(null);
		});

		if (this.elements.mainClose) {
			this.elements.mainClose.addEventListener('click', (e) => {
				this.closeRun(null);
				e.preventDefault();
			});
		}

		this.elements.mainBasket.addEventListener('click', (e) => {
			this.closeRun(this.flags.isBasketPage ? null :'/basket/');
			e.preventDefault();
		});

		if (this.elements.skipToBasket) {
			this.elements.skipToBasket.addEventListener('click', (e) => {
				this.closeRun('/basket/');
				e.preventDefault();
			});
		}

		this.attachProductLinkEvents();

		this.elements.mainFooter.style.right = this.getScrollWidth() + 'px';

		this.setCarouselWidths();
	}

	private setCarouselWidths(container: HTMLElement = this.elements.wrapper) {
		Helpers.scopedLoop(container.querySelectorAll('[data-atb-main-list]'), function (element: HTMLLinkElement, i: Number, context: any) {
			const itemCount = element.querySelectorAll('[data-atb-main-item]').length;
			element.style.width = (itemCount * context.mobileItemWidth) + 'px';
		}, this);
	}

	private attachProductLinkEvents() {
		const productCards = this.elements.wrapper.querySelectorAll('product-card');
		const attachedCssClass = 'c-product-card--detail-click-attached';

		for (let i = 0; i < productCards.length; i++) {
			if (productCards[i].classList.contains(attachedCssClass)) {
				continue;
			}
			/*@ts-ignore */
			productCards[i].productLinkOnClick = (event) => {
				const target = event.target as HTMLLinkElement;
				event.preventDefault();
				this.openDetail(target);
				this.elementWhichOpenedDetail = target;
			};
			productCards[i].classList.add(attachedCssClass);
		}
	}

	getCustomersAlsoBought(productName: string) {
		fetch(`/api/customersalsobought?name=${productName}&ignoreYouMayAlsoLikeExclusions=True`)
			.then((response) => {
				if (response.ok) {
					return response.json();
				} else {
					throw new Error('Network response was not ok retrieving customers also bought in the add to basket overlay.');
				}
			})
			.then((data) => {
				if (data && data.Products && data.Products.length) {
					const customersAlsoBoughtHtml = this.getTemplate('addToBasketOverlayCustomersAlsoBought', { CustomersAlsoBought: data.Products });
					this.elements.customersAlsoBoughtPlaceholder.innerHTML = customersAlsoBoughtHtml;
					this.attachProductLinkEvents();
					this.setCarouselWidths(this.elements.customersAlsoBoughtPlaceholder);
					const firstLinkInATB = Helpers.isUsingKeyboard() ? this.elements.skipToBasket : document.querySelector('.atb a') as HTMLLinkElement;
					if (firstLinkInATB && document.activeElement !== firstLinkInATB) {
						this.focusOnFirstItem();
					}
				} else {
					Helpers.empty(this.elements.customersAlsoBoughtPlaceholder);
				}
			})
			.catch((error) => {
				Logger.trackException(error);
				Helpers.empty(this.elements.customersAlsoBoughtPlaceholder);
			});
	}

	focusOnFirstItem() {
		const firstLinkInATB = Helpers.isUsingKeyboard() ? this.elements.skipToBasket : document.querySelector('.atb a') as HTMLLinkElement;
		if (firstLinkInATB) {
			firstLinkInATB.focus();
		} else {
			const firstFocusableElement = SelectorFilter.excludeByClass(this.elements.wrapper.querySelectorAll('a, button'), "c-skip");
			if (firstFocusableElement.length) {
				firstFocusableElement[0].focus();
			}
		}
	}


	setElementsForOverlay() {
		this.elements.wrapper = document.querySelector('[data-atb]');
		this.elements.background = document.querySelector('[data-atb-background]');
		this.elements.main = document.querySelector('[data-atb-main]');
		this.elements.mainInner = document.querySelector('[data-atb-main-inner]');
		this.elements.mainList = document.querySelectorAll('[data-atb-main-list]');
		this.elements.mainListWrapper = document.querySelector('[data-atb-main-list-wrapper]');
		this.elements.mainItem = document.querySelectorAll('[data-atb-main-item]');
		this.elements.mainFooter = document.querySelector('[data-atb-main-footer]');
		this.elements.mainFooterItems = document.querySelector('[data-atb-main-footer-items]');
		this.elements.mainFooterPrice = document.querySelector('[data-atb-main-footer-price]');
		this.elements.mainForm = document.querySelectorAll('[data-atb-main-form]');
		this.elements.mainClose = document.querySelector('[data-atb-main-close]');
		this.elements.mainBasket = document.querySelector('[data-atb-main-basket]');
		this.elements.customersAlsoBoughtPlaceholder = this.elements.wrapper.querySelector('[data-atb-customers-also-bought-placeholder]');
		this.elements.skipToBasket = document.querySelector('[data-atb-main-skip-to-basket]');
		this.elements.productQuantity = document.querySelector('[data-atb-main-product-quantity]');
		this.elements.productLink = this.elements.wrapper.querySelectorAll('.js-product-link');
		this.elements.detail = document.querySelector('[data-product-viewer]');
		this.elements.detailFade = document.querySelector('[data-product-viewer-fade]');
		this.elements.detailInner = document.querySelector('[data-product-viewer-inner]');
		this.elements.detailContainer = document.querySelector('[data-product-viewer-container]');
		this.elements.detailLoader = document.querySelector('[data-product-viewer-loader]');

		if (this.elements.wrapper) {
			this.elements.wrapper.style.display = 'block';
		}
	}

	setHeights() {

		if (this.flags.overlayActive) {

			let viewportHeight = window.innerHeight;
			document.documentElement.style.height = viewportHeight + 'px';
			document.body.style.height = (viewportHeight - 1) + 'px';

		}

	}

	removeHeights() {

		document.documentElement.style.height = 'auto';
		document.body.style.height = 'auto';

	}

	orientationChange() {
		if (this.elements.wrapper) {
			this.kickScroll(this.elements.mainInner);
			this.kickScroll(this.elements.mainListWrapper);
			this.kickScroll(this.elements.detailInner);
		}
	}

	openDetail(element: HTMLElement) {

		if (!this.flags.allowInteraction) return;

		const item = element.closest('[data-atb-main-item]');
		const id = item.getAttribute('data-atb-main-item');
		const quantityElement = item.querySelector('[data-atb-main-product-quantity-widget-value]') as HTMLInputElement;
		const quantity = quantityElement ? quantityElement.value : 0;

		if ((id !== this.state.productId) || !this.flags.detailActive) {

			this.state.productId = id;
			this.flags.allowInteraction = false;
			this.allowAnimations();

			this.elements.detailContainer.innerHTML = "<product-viewer productId='" + id + "' initialQuantity='" + quantity + "'></product-viewer>";

			this.elements.detailItem = document.querySelector('[data-product-viewer-item]');
			this.elements.detailItemSpecs = document.querySelector('[data-product-viewer-item-specs]');
			this.elements.detailItemSpecsTitle = document.querySelector('[data-product-viewer-item-specs-title]');
			this.elements.detailItemSpecsContent = document.querySelector('[data-product-viewer-item-specs-content]');
			this.elements.detailItemView = document.querySelector('[data-product-viewer-item-view]');

			if (this.elements.detailItemView) {
				this.elements.detailItemView.addEventListener('click', (e) => {

					const url = this.elements.detailItemView.getAttribute('href');

					this.closeRun(url);
					e.preventDefault();

				});
			}

			if (!this.flags.detailActive) {

				this.elements.main.classList.add('atb__main--detail-active');
				this.elements.detail.classList.remove('c-slidy-outy__detail--hidden');
				this.elements.detail.classList.add('c-slidy-outy__detail--active');

				setTimeout(() => {

					this.flags.detailActive = true;
					this.flags.allowInteraction = true;
					this.kickScroll(this.elements.mainInner);
					this.kickScroll(this.elements.mainListWrapper);
					this.kickScroll(this.elements.detailInner);
					this.disallowAnimations();
					this.mainOverlayTrapFocus.removeListener();

				}, this.animationTime);

			} else {

				this.flags.allowInteraction = true;

				this.kickScroll(this.elements.mainInner);
				this.kickScroll(this.elements.mainListWrapper);
				this.kickScroll(this.elements.detailInner);
				this.disallowAnimations();

			}

		}


	}

	closeDetail() {

		if (!this.flags.allowInteraction || !this.flags.detailActive) return;

		this.flags.allowInteraction = false;
		this.allowAnimations();
		this.mainOverlayTrapFocus = new TrapFocus(document.querySelector('.atb__main'), document.querySelector('.atb__main__footer__basket__button'));
		this.elementWhichOpenedDetail.focus();

		this.elements.main.classList.remove('atb__main--detail-active');
		this.elements.detail.classList.remove('c-slidy-outy__detail--active');

		setTimeout(() => {

			this.flags.detailActive = false;
			this.flags.allowInteraction = true;
			this.kickScroll(this.elements.mainInner);
			this.kickScroll(this.elements.mainListWrapper);
			this.disallowAnimations();
			this.elements.detailContainer.innerHTML = '';
			this.elements.detailItem = null;
			this.elements.detail.classList.add('c-slidy-outy__detail--hidden');
		}, this.animationTime);

	}

	updatePrice() {

		let totalQuantity = 0;
		let totalPrice = 0;


		for (let i = 0; i < (<any>window).addedProducts.length; i++) {
			const currentProduct = (<any>window).addedProducts[i];
			totalQuantity = totalQuantity + currentProduct.quantity;
			totalPrice = totalPrice + (currentProduct.price * currentProduct.quantity);
		}

		this.elements.mainFooterItems.innerHTML = totalQuantity + '';
		const priceFormatted = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2, minimumFractionDigits: totalPrice % 1 == 0 ? 0 : 2 }).format(totalPrice);
		this.elements.mainFooterPrice.innerHTML = priceFormatted;
	}

	initSlider() {
		try {
			if (document.querySelectorAll('[data-product-viewer-item-slideshow-item]').length > 1) {
				this.instances.slideshow = new Splide('[data-product-viewer-item-slideshow-container]', {
					type: 'loop',
					perPage: 1,
					perMove: 1,
					speed: 500,
					interval: 5000,
					pagination: true,
					arrows: true,
					keyboard: 'focused',
					arrowPath: 'M1.14 39.85a1.12 1.12 0 01-.72-.26 1.17 1.17 0 01-.16-1.63l14.72-18.11L.24 1.78A1.15 1.15 0 01.6.19a1.14 1.14 0 011.44.16l15.94 19.5L2.04 39.43a1.15 1.15 0 01-.9.42z',
					classes: {
						prev: 'c-carousel__button c-carousel__button--left js-carousel-button-left',
						next: 'c-carousel__button c-carousel__button--right js-carousel-button-right',
						pagination: 'c-carousel__pagination',
						page: 'c-carousel__pagination-page'
					}
				});

				this.instances.slideshow.on('mounted', () => {
					setTimeout(() => {
						const containerParentElement = document.querySelector('[data-product-viewer-item-slideshow-container]');
						containerParentElement.removeAttribute('tabindex');
						const slides = containerParentElement.querySelectorAll('.splide__slide');
						for (var i = 0; i < slides.length; i++) { slides[i].removeAttribute('tabindex'); }
					}, 1000);
				});

				this.instances.slideshow.mount();
			} else {
				document.querySelector('[data-product-viewer-item-slideshow-container]').classList.add('c-carousel--single');
			}

		} catch (error) {
			Logger.trackException(error);
		}
	}

	destroySlider() {
		if (this.instances.slideshow) {
			this.instances.slideshow.destroy();
			this.instances.slideshow = null;
		}
	}

}

